import React, { useState, useEffect } from 'react';
import { useAppConfig } from './useAppConfig';
import { useTheme, fontFamily } from './theme';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoint}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
}

function App() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [user, setUser] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [showCustomRequest, setShowCustomRequest] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customYear, setCustomYear] = useState('');
  const [customType, setCustomType] = useState('movie');
  const [customNote, setCustomNote] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const config = useAppConfig();
  const { theme, darkMode, toggleDarkMode } = useTheme();
  const isMobile = useIsMobile();

  const search = async (e) => {
    e.preventDefault();
    setHasSearched(true);
    setMessage('');
    setResults([]);
    setLoading(true);
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/search?query=${encodeURIComponent(query)}`);
      const data = await res.json();
      setResults(data.results || []);
    } finally {
      setLoading(false);
    }
  };

  const requestselection = async (item) => {
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user,
          note: customNote || note,
          selection: {
            id: item.id,
            title: item.title || item.name,
            year: item.year,
            media_type: item.media_type,
          },
        }),
      });
      if (res.status === 409) {
        const data = await res.json();
        setMessage(data.error || 'This item has already been requested.');
        return;
      }
      setMessage(`requested selection: ${item.title || item.name}`);
      setNote('');
    } catch (err) {
      setMessage('Request failed. Please try again.');
    }
  };

  const inputStyle = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '10px 14px',
    borderRadius: 6,
    border: `1px solid ${theme.borderColor}`,
    fontSize: 16, // 16px stops iOS from zooming on focus
    background: theme.inputBg,
    color: theme.text,
    transition: 'background 0.2s, color 0.2s',
  };

  const headerBtnStyle = {
    background: theme.cardBg,
    border: `1.5px solid ${theme.borderColor}`,
    borderRadius: 6,
    padding: isMobile ? '8px 12px' : '8px 18px',
    fontWeight: 600,
    fontSize: isMobile ? 14 : 15,
    cursor: 'pointer',
    boxShadow: '0 2px 8px 0 rgba(60,72,88,0.06)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 0.2s, color 0.2s',
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 1100,
        boxSizing: 'border-box',
        margin: isMobile ? 0 : '2.5rem auto',
        fontFamily,
        background: theme.bgColor,
        minHeight: '100vh',
        padding: isMobile ? '1.25rem 16px 3rem' : '2rem 2rem 4rem',
        borderRadius: isMobile ? 0 : 18,
        boxShadow: isMobile ? 'none' : theme.shadow,
        color: theme.text,
        transition: 'background 0.3s, color 0.3s',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h1
          style={{
            color: theme.mainColor,
            fontWeight: 700,
            letterSpacing: '-1px',
            fontSize: isMobile ? 26 : 36,
            lineHeight: 1.2,
            margin: 0,
            flex: isMobile ? '1 1 100%' : '1 1 auto',
            minWidth: 0,
          }}
        >
          Movie Show Requester
        </h1>
        <div style={{ display: 'flex', gap: 8, marginLeft: isMobile ? 0 : 'auto' }}>
          <a
            href="/admin"
            style={{ ...headerBtnStyle, color: theme.subtext }}
            onMouseOver={(e) => { e.currentTarget.style.color = theme.mainColor; }}
            onMouseOut={(e) => { e.currentTarget.style.color = theme.subtext; }}
            title="Admin page"
          >
            Go to Admin
          </a>
          <button onClick={toggleDarkMode} style={{ ...headerBtnStyle, color: theme.text }} title="Toggle dark mode">
            {darkMode ? '🌙 Dark' : '☀️ Light'}
          </button>
        </div>
      </div>

      <p style={{ color: theme.subtext, margin: '0 0 24px' }}>Search for desired movie or show</p>

      <form
        onSubmit={search}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '180px minmax(0, 1fr) 300px auto',
          gap: 12,
          alignItems: 'center',
          marginBottom: 32,
        }}
      >
        <input placeholder="Your name (optional)" value={user} onChange={(e) => setUser(e.target.value)} style={inputStyle} />
        <input placeholder="Search for a movie or show" value={query} onChange={(e) => setQuery(e.target.value)} required style={inputStyle} />
        <input placeholder="Add a note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} />
        <button
          type="submit"
          style={{
            width: isMobile ? '100%' : 'auto',
            background: theme.buttonBg,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 6,
            padding: '12px 24px',
            fontWeight: 600,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px 0 rgba(25,118,210,0.08)',
          }}
        >
          Search
        </button>
      </form>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '1.5rem 0', fontSize: 18, color: theme.mainColor, fontWeight: 500 }}>
          <span style={{ width: 28, height: 28, border: `4px solid ${theme.mainColor}`, borderTop: '4px solid transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
          Searching...
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
        </div>
      )}

      {message && (
        <div style={{ color: theme.successText, background: theme.successBg, borderRadius: 6, padding: '10px 16px', marginBottom: 24, fontWeight: 500 }}>
          {message}
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {results
          .filter((item) => item.media_type !== 'collection')
          .map((item) => {
            const posterUrl = item.poster_path && item.poster_path !== 'N/A' ? item.poster_path : '/Unknown_Poster_w80.png';
            const mediaType = item.media_type === 'movie' ? 'Movie' : item.media_type === 'series' ? 'Show' : item.media_type;

            return (
              <li
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? 12 : 20,
                  margin: isMobile ? '1rem 0' : '1.5rem 0',
                  background: theme.cardBg,
                  border: `1px solid ${theme.borderColor}`,
                  borderRadius: 10,
                  boxShadow: '0 2px 8px 0 rgba(60,72,88,0.06)',
                  padding: isMobile ? 12 : 18,
                }}
              >
                <img
                  src={posterUrl}
                  alt={item.title}
                  style={{
                    width: isMobile ? 64 : 80,
                    height: isMobile ? 96 : 120,
                    flexShrink: 0,
                    objectFit: 'cover',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px 0 rgba(60,72,88,0.10)',
                    background: '#eee',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 8px', fontWeight: 600, fontSize: isMobile ? 17 : 22, lineHeight: 1.3, color: theme.text }}>
                    <span style={{ overflowWrap: 'anywhere' }}>{item.title}</span>
                    <span style={{ color: '#888', fontSize: isMobile ? 14 : 16, fontWeight: 400 }}>({item.year})</span>
                    {item.inPlex && (
                      <span style={{ background: '#ffc107', color: '#222', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}>
                        Already in Plex
                      </span>
                    )}
                  </div>
                  <div style={{ fontStyle: 'italic', color: theme.mainColor, margin: '4px 0 8px', fontSize: 15 }}>{mediaType}</div>
                  <div
                    style={{
                      margin: '6px 0 12px',
                      color: theme.subtext,
                      fontSize: isMobile ? 14 : 16,
                      lineHeight: 1.5,
                      ...(isMobile && {
                        display: '-webkit-box',
                        WebkitLineClamp: 6,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                    }}
                  >
                    {item.overview}
                  </div>
                  <button
                    onClick={() => requestselection(item)}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      background: theme.buttonBg,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '11px 18px',
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: 'pointer',
                    }}
                  >
                    Request this
                  </button>
                </div>
              </li>
            );
          })}
      </ul>

      {hasSearched && !loading && (
        <button
          style={{ marginTop: 24, width: isMobile ? '100%' : 'auto', background: theme.buttonBg, color: theme.buttonText, border: 'none', borderRadius: 6, padding: '12px 24px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
          onClick={() => setShowCustomRequest(true)}
        >
          Submit a Custom Request
        </button>
      )}

      <hr style={{ margin: isMobile ? '2rem 0' : '3rem 0', border: 'none', borderTop: `1px solid ${theme.borderColor}` }} />

      {showCustomRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div
            style={{
              background: theme.cardBg,
              borderRadius: 10,
              padding: isMobile ? 20 : 32,
              maxWidth: 400,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxSizing: 'border-box',
              boxShadow: theme.shadow,
            }}
          >
            <h2 style={{ color: theme.mainColor, margin: '0 0 20px', fontSize: isMobile ? 20 : 24 }}>Custom Movie/Show Request</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await requestselection({ id: `custom-${Date.now()}`, title: customTitle.trim(), year: customYear.trim(), media_type: customType });
                setShowCustomRequest(false);
                setCustomTitle('');
                setCustomYear('');
                setCustomType('movie');
                setCustomNote('');
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Type</label>
                <select value={customType} onChange={(e) => setCustomType(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
                  <option value="movie">Movie</option>
                  <option value="series">TV Show</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Title</label>
                <input type="text" required value={customTitle} onChange={(e) => setCustomTitle(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Year</label>
                <input type="number" min="1800" max="2099" step="1" value={customYear} onChange={(e) => setCustomYear(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} placeholder="e.g. 2023" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Note (optional)</label>
                <input type="text" value={customNote} onChange={(e) => setCustomNote(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} placeholder="Any extra info" />
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
                <button type="submit" style={{ flex: 1, background: theme.buttonBg, color: theme.buttonText, border: 'none', borderRadius: 6, padding: '12px 24px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Submit</button>
                <button type="button" onClick={() => setShowCustomRequest(false)} style={{ flex: 1, background: theme.deleteBg, color: '#fff', border: 'none', borderRadius: 6, padding: '12px 24px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer style={{ marginTop: 48, color: theme.footer, textAlign: 'center', fontSize: 14 }}>
        &copy; {new Date().getFullYear()} Movie Show Requester
      </footer>
    </div>
  );
}

export default App;